import {
  ButtonDropdownProps,
  Icon,
  SpaceBetween,
  TopNavigation,
  TopNavigationProps,
} from '@amzn/awsui-components-react';
import { memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { isProfileEmpty } from '@/src/components/game/Account/JamProfileCheckGuard';
import { ILanguageSelectProps, LanguageSelect, ThemeToggle } from '@/src/components/ui/molecules/Header';
import { useUser } from '@/src/store/user.context';

// styles
import { MobileHeader } from '@/src/components/ui/organisms/Header/MobileHeader/MobileHeader';
import { UserButton } from '@/src/components/ui/organisms/Header/UserButton';
import { LOGO_SVG } from '@/src/constants/common';
import { useIsMobile } from '@/src/hooks/useIsMobile';
import { RoutePath } from '@/src/RoutePath';
import { joinClasses } from '@/src/utils/scss.utils';
import { i18nKeys } from '@/src/utils/i18n.utils';
import type { CancelableEventHandler } from '@amzn/awsui-components-react/polaris/internal/events';
import { useHistory } from 'react-router-dom';
import styles from './Header.module.scss';
import { useToolPanel } from '@/src/store/tool-panel.context';
import { config } from '@/src/config/app-config';
import { useJamEventDetails } from '@/src/store/jam-event-details.context';
import { useAITeammate } from '@/src/hooks/store/useAITeammate';

export interface NavSubMenuItem {
  id: string;
  text: string;
  description: string;
  href?: string;
  imageUrl?: string;
}

export interface NavMenuItem {
  id: string;
  text: string;
  href?: string;
  subMenuItems?: NavSubMenuItem[];
}

export interface UserMenuItem {
  id: string;
  text: string;
  href?: string;
}

export interface IHeaderProps {
  navItems: NavMenuItem[];
  showThemeToggle?: boolean;
  lang: ILanguageSelectProps;
  onMenuItemClick: CancelableEventHandler<ButtonDropdownProps.ItemClickDetails | object>;
}

const i18String = {
  searchIconAriaLabel: 'Search',
  searchDismissIconAriaLabel: 'Close search',
  overflowMenuTriggerText: 'More',
  overflowMenuTitleText: 'All',
  overflowMenuBackIconAriaLabel: 'Back',
  overflowMenuDismissIconAriaLabel: 'Close menu',
};

export const Header = memo<IHeaderProps>(({ navItems, lang, onMenuItemClick }: IHeaderProps) => {
  const { t } = useTranslation();
  const { user } = useUser();
  const history = useHistory();
  const isMobile = useIsMobile();
  const toolPanel = useToolPanel();
  const { event } = useJamEventDetails();
  const { aiTeammateActive } = useAITeammate(event?.eventName || '')();

  const handleLogoClick = useCallback<CancelableEventHandler>(
    (e) => {
      e.preventDefault();
      if (location.pathname === RoutePath.ROOT) return;
      history.push(RoutePath.ROOT);
    },
    [history, location]
  );

  const identity = useMemo(
    () => ({
      title: 'Jam',
      href: '/',
      logo: {
        src: LOGO_SVG,
        alt: 'AWS',
      },
      onFollow: handleLogoClick,
    }),
    []
  );

  const navMenu = useMemo<TopNavigationProps.Utility[]>(() => {
    return navItems.map((item) => {
      if (item.subMenuItems && item.subMenuItems.length > 0) {
        return {
          id: item.id,
          type: 'menu-dropdown',
          ariaLabel: t(item.text),
          text: t(item.text),
          onItemClick: onMenuItemClick,
          items: item.subMenuItems.map((subItem) => {
            return {
              id: subItem.id,
              text: t(subItem.text),
              description: t(subItem.description),
              href: subItem.href,
              onClick: onMenuItemClick,
            };
          }) as ButtonDropdownProps.Item[],
        };
      } else {
        return {
          type: 'button',
          id: item.id,
          ariaLabel: t(item.text),
          text: t(item.text),
          href: item.href,
          onClick: onMenuItemClick,
        };
      }
    });
  }, [navItems, t]);

  // if user is not have jam profile. we will show top-navigation empty
  if (user && isProfileEmpty(user)) {
    return (
      <div className="topnav no-user">
        <TopNavigation identity={identity} i18nStrings={i18String} />
      </div>
    );
  }

  if (isMobile) {
    return <MobileHeader />;
  }

  return (
    <div id={styles.topnav}>
      <TopNavigation identity={identity} i18nStrings={i18String} utilities={navMenu} />
      <div className={joinClasses(styles.aside, 'awsui-polaris-dark-mode')} style={{ backgroundColor: '#0F1B2A' }}>
        <SpaceBetween direction="horizontal" size="s" alignItems="center">
          {config.ff.enableAiTeammate && aiTeammateActive && (
            <button
              className={styles.aiTeammateButton}
              aria-label={t(i18nKeys.header.aiTeammate.toggle)}
              onClick={() => {
                toolPanel.toggleAITeammateChat(true);
              }}
            >
              <span className={styles.aiTeammateIcon}>
                <Icon name="gen-ai" />
              </span>
              {t(i18nKeys.header.aiTeammate.label)}
            </button>
          )}
          <ThemeToggle />
          <UserButton />
          <LanguageSelect {...lang} />
        </SpaceBetween>
      </div>
    </div>
  );
});

Header.displayName = 'Header';
